/**
 * Manejo centralizado de errores para la API
 * Proporciona funciones para manejar, transformar y mostrar errores de manera consistente
 */

import { HTTP_STATUS } from '../config/api';

/**
 * Tipos de errores específicos de la aplicación
 */
export const ERROR_TYPES = {
  NETWORK: 'NETWORK_ERROR',
  AUTHENTICATION: 'AUTHENTICATION_ERROR',
  AUTHORIZATION: 'AUTHORIZATION_ERROR',
  VALIDATION: 'VALIDATION_ERROR',
  NOT_FOUND: 'NOT_FOUND_ERROR',
  CONFLICT: 'CONFLICT_ERROR',
  RATE_LIMIT: 'RATE_LIMIT_ERROR',
  SERVER: 'SERVER_ERROR',
  TIMEOUT: 'TIMEOUT_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR'
};

/**
 * Mapeo de códigos HTTP a tipos de error
 */
const STATUS_TO_ERROR_TYPE = {
  [HTTP_STATUS.BAD_REQUEST]: ERROR_TYPES.VALIDATION,
  [HTTP_STATUS.UNAUTHORIZED]: ERROR_TYPES.AUTHENTICATION,
  [HTTP_STATUS.FORBIDDEN]: ERROR_TYPES.AUTHORIZATION,
  [HTTP_STATUS.NOT_FOUND]: ERROR_TYPES.NOT_FOUND,
  [HTTP_STATUS.CONFLICT]: ERROR_TYPES.CONFLICT,
  [HTTP_STATUS.UNPROCESSABLE_ENTITY]: ERROR_TYPES.VALIDATION,
  [HTTP_STATUS.TOO_MANY_REQUESTS]: ERROR_TYPES.RATE_LIMIT,
  [HTTP_STATUS.INTERNAL_SERVER_ERROR]: ERROR_TYPES.SERVER,
  [HTTP_STATUS.BAD_GATEWAY]: ERROR_TYPES.SERVER,
  [HTTP_STATUS.SERVICE_UNAVAILABLE]: ERROR_TYPES.SERVER
};

/**
 * Mensajes de error por defecto en español
 */
const DEFAULT_ERROR_MESSAGES = {
  [ERROR_TYPES.NETWORK]: 'Error de conexión. Verifica tu conexión a internet.',
  [ERROR_TYPES.AUTHENTICATION]: 'Error de autenticación. Por favor, inicia sesión nuevamente.',
  [ERROR_TYPES.AUTHORIZATION]: 'No tienes permisos para realizar esta acción.',
  [ERROR_TYPES.VALIDATION]: 'Los datos proporcionados no son válidos.',
  [ERROR_TYPES.NOT_FOUND]: 'El recurso solicitado no fue encontrado.',
  [ERROR_TYPES.CONFLICT]: 'Conflicto con el estado actual del recurso.',
  [ERROR_TYPES.RATE_LIMIT]: 'Demasiadas solicitudes. Intenta nuevamente en unos minutos.',
  [ERROR_TYPES.SERVER]: 'Error interno del servidor. Intenta nuevamente más tarde.',
  [ERROR_TYPES.TIMEOUT]: 'La solicitud ha excedido el tiempo límite.',
  [ERROR_TYPES.UNKNOWN]: 'Ha ocurrido un error inesperado.'
};

/**
 * Clase para errores específicos de la API
 */
export class ApiError extends Error {
  constructor(message, type, status, data = null, originalError = null) {
    super(message);
    this.name = 'ApiError';
    this.type = type;
    this.status = status;
    this.data = data;
    this.originalError = originalError;
    this.timestamp = new Date().toISOString();
  }

  /**
   * Convierte el error a un objeto serializable
   */
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      type: this.type,
      status: this.status,
      data: this.data,
      timestamp: this.timestamp
    };
  }

  /**
   * Retorna un mensaje amigable para el usuario
   */
  getUserMessage() {
    // Si el error tiene un mensaje específico del servidor, usarlo
    if (this.data && this.data.message) {
      return this.data.message;
    }
    
    // Si el error tiene un mensaje específico del campo, usarlo
    if (this.data && this.data.errors && Array.isArray(this.data.errors)) {
      return this.data.errors.map(err => err.message || err).join(', ');
    }
    
    // Usar mensaje por defecto basado en el tipo
    return DEFAULT_ERROR_MESSAGES[this.type] || this.message;
  }
}

/**
 * Determina el tipo de error basado en el status y otros factores
 */
export const determineErrorType = (error) => {
  // Error de red (sin respuesta del servidor)
  if (!error.response && (error.code === 'NETWORK_ERROR' || error.name === 'TypeError')) {
    return ERROR_TYPES.NETWORK;
  }
  
  // Error de timeout
  if (error.name === 'AbortError' || error.code === 'TIMEOUT') {
    return ERROR_TYPES.TIMEOUT;
  }
  
  // Error basado en status HTTP
  if (error.response && error.response.status) {
    const status = error.response.status;
    return STATUS_TO_ERROR_TYPE[status] || ERROR_TYPES.UNKNOWN;
  }
  
  return ERROR_TYPES.UNKNOWN;
};

/**
 * Extrae información del error de la respuesta
 */
export const extractErrorInfo = (error) => {
  let message = error.message;
  let data = null;
  let status = null;

  if (error.response) {
    status = error.response.status;
    data = error.response.data;
    
    // Intentar extraer mensaje más específico
    if (data) {
      if (typeof data === 'string') {
        message = data;
      } else if (data.message) {
        message = data.message;
      } else if (data.error) {
        message = data.error;
      } else if (data.errors && Array.isArray(data.errors)) {
        message = data.errors.map(err => err.message || err).join(', ');
      }
    }
  }

  return { message, data, status };
};

/**
 * Transforma un error nativo en un ApiError
 */
export const transformError = (error) => {
  // Si ya es un ApiError, retornarlo
  if (error instanceof ApiError) {
    return error;
  }

  const type = determineErrorType(error);
  const { message, data, status } = extractErrorInfo(error);

  return new ApiError(message, type, status, data, error);
};

/**
 * Maneja errores de manera centralizada
 */
export const handleError = (error, options = {}) => {
  const {
    showToast = true,
    logError = true,
    throwError = true,
    customMessage = null
  } = options;

  // Transformar error
  const apiError = transformError(error);

  // Log del error
  if (logError) {
    console.error('[ErrorHandler] Error capturado:', {
      type: apiError.type,
      status: apiError.status,
      message: apiError.message,
      data: apiError.data,
      originalError: apiError.originalError
    });
  }

  // Mostrar toast si está configurado
  if (showToast && typeof window !== 'undefined') {
    const message = customMessage || apiError.getUserMessage();
    
    // Intentar usar el sistema de toast de la aplicación
    if (window.showToast) {
      window.showToast(message, 'error');
    } else {
      // Fallback a console si no hay sistema de toast
      console.error(`[Toast] ${message}`);
    }
  }

  // Lanzar error si está configurado
  if (throwError) {
    throw apiError;
  }

  return apiError;
};

/**
 * Hook para manejar errores en componentes React
 */
export const useErrorHandler = () => {
  const handleApiError = (error, options = {}) => {
    return handleError(error, options);
  };

  const handleAsyncError = async (asyncFn, options = {}) => {
    try {
      return await asyncFn();
    } catch (error) {
      return handleApiError(error, options);
    }
  };

  return {
    handleError: handleApiError,
    handleAsyncError,
    transformError,
    ApiError
  };
};

/**
 * Interceptor de errores para el cliente API
 */
export const createErrorInterceptor = (options = {}) => {
  return (error) => {
    return handleError(error, { throwError: false, ...options });
  };
};

/**
 * Verifica si un error es de un tipo específico
 */
export const isErrorType = (error, type) => {
  const apiError = error instanceof ApiError ? error : transformError(error);
  return apiError.type === type;
};

/**
 * Verifica si un error es recuperable (puede ser reintentado)
 */
export const isRecoverableError = (error) => {
  const apiError = error instanceof ApiError ? error : transformError(error);
  const recoverableTypes = [
    ERROR_TYPES.NETWORK,
    ERROR_TYPES.TIMEOUT,
    ERROR_TYPES.RATE_LIMIT,
    ERROR_TYPES.SERVER
  ];
  return recoverableTypes.includes(apiError.type);
};

/**
 * Obtiene sugerencias de acción para el usuario basado en el tipo de error
 */
export const getErrorSuggestions = (error) => {
  const apiError = error instanceof ApiError ? error : transformError(error);
  
  const suggestions = {
    [ERROR_TYPES.NETWORK]: [
      'Verifica tu conexión a internet',
      'Intenta recargar la página',
      'Contacta al soporte si el problema persiste'
    ],
    [ERROR_TYPES.AUTHENTICATION]: [
      'Inicia sesión nuevamente',
      'Verifica que tu sesión no haya expirado',
      'Contacta al soporte si no puedes acceder'
    ],
    [ERROR_TYPES.AUTHORIZATION]: [
      'Verifica que tengas los permisos necesarios',
      'Contacta al administrador del sistema',
      'Intenta acceder con una cuenta diferente'
    ],
    [ERROR_TYPES.VALIDATION]: [
      'Revisa los datos ingresados',
      'Verifica que todos los campos estén completos',
      'Consulta la documentación para formatos válidos'
    ],
    [ERROR_TYPES.RATE_LIMIT]: [
      'Espera unos minutos antes de intentar nuevamente',
      'Reduce la frecuencia de tus solicitudes',
      'Contacta al soporte si necesitas límites más altos'
    ],
    [ERROR_TYPES.SERVER]: [
      'Intenta nuevamente en unos minutos',
      'El problema es temporal',
      'Contacta al soporte si persiste'
    ]
  };

  return suggestions[apiError.type] || ['Contacta al soporte técnico'];
};